import { FilePondFile } from "filepond";
import { FGCustomPanel, FGListen, FGNumberInput, FGSelectInput, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import styled from "styled-components";
import * as Yup from "yup";

import { DogTitlesApi, EPlatform, ErrorDetails, TransientDogTitlesApi } from "../../../../api";
import { FGCountrySelect } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";

export interface IDrupalDogTitlesPanelProps {
  onTitleSelected: (title: any) => void;
  dogId: number;
  onSaved: () => void;
}

const StyledContainer = styled.div`
  margin-top: 1rem;
`;

export const DrupalDogTitlesPanel: React.FunctionComponent<IDrupalDogTitlesPanelProps> = ({ dogId, onSaved }) => {
  const { t, tUnsafe } = useTl();
  const { currentLanguage } = useGlobalData();
  const dogTitleApi = useApiService(DogTitlesApi);
  const [titleFile, setTitleFile] = React.useState<FilePondFile>(null);
  const [year, setYear] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      titleId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("title-available", t(ETLCodes.DogAlreadyHasTitle), async function (value) {
          if (!value) return false;
          const res = await dogTitleApi.isTitleAvailable(dogId, value, year ?? 0);
          return res.value;
        }),
      countryId: Yup.number().nullable(),
      year: Yup.number().required(t(ETLCodes.Required)),
    });
  }, [dogId, dogTitleApi, t]);
  const titleTransientApi = useApiService(TransientDogTitlesApi);
  const [countryId, setCountryId] = React.useState<number>();
  const [titles, tloading] = useReferential(
    (a) => a.getTitles(currentLanguage.languageId, EPlatform.PORTAL, countryId, null),
    true,
    [currentLanguage.languageId, countryId]
  );

  const save = React.useCallback(
    async (data: any) => {
      //onTitleSelected(data);
      // call backend and save your data
      setSaving(true);
      try {
        const res = await titleTransientApi
          .create(
            data.dogTitlesTransientId ?? 0,
            dogId,
            data.titleId,
            data.year,
            data.managerId,
            data.countryId,
            titleFile ? { data: titleFile.file, fileName: titleFile.filename } : null
          )
          .then(() => onSaved());
        setSaving(false);
        showSuccess(t(ETLCodes.SaveSuccess));
        return res;
      } catch (error) {
        setSaving(false);
        const details: ErrorDetails = JSON.parse(error.response);
        if (details?.errorCode) {
          showError(tUnsafe(`BackendError_${details.errorCode}`));
        } else {
          showError(t(ETLCodes.Error));
        }
      }

      //when done
    },
    [dogId, onSaved, t, tUnsafe, titleFile, titleTransientApi]
  );

  const initialData = React.useMemo(() => {
    return { dogId: dogId, resultIds: [] };
  }, [dogId]);

  return (
    <StyledContainer>
      <DrupalFormGenerator
        onSubmit={save}
        initialValues={initialData}
        editMode={true}
        validationSchema={FormSchema}
        showDeleteButton={false}
        hideButtons={false}
        minLabelWidth={110}
        saving={saving}
      >
        <FGListen field="countryId" onChanged={(value) => setCountryId(value)} />
        <FGListen field="year" onChanged={(value) => setYear(value)} />
        <FGCustomPanel>
          {(ctx) => (
            <FilePond
              labelIdle={t(ETLCodes.FilePondDragOrSelect)}
              onupdatefiles={(f) => {
                ctx.formik.setFieldValue("titleFile", f.length > 0 ? f[0] : null);
                setTitleFile(f.length > 0 ? f[0] : null);
              }}
            />
          )}
        </FGCustomPanel>
        <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} includeBelgium={false} />
        <FGSelectInput name="titleId" items={titles} loading={tloading} label={t(ETLCodes.Title)} />
        <FGNumberInput label={t(ETLCodes.Year)} name="year" min={1000} max={9999} />
      </DrupalFormGenerator>
    </StyledContainer>
  );
};
